import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, PhoneOff, Mic, MicOff, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORO = '#c9a84c';

// El teléfono del CRM.
//
// El navegador habla SIP por WebSocket contra el Asterisk del servidor, que saca
// la llamada por Zadarma con el número de eHome (+34 919 934 659). Graba,
// transcribe y lo vuelca en la ficha al colgar; de eso se encarga el servidor.
//
// Va como contexto para que se pueda llamar desde cualquier sitio (la sección
// Llamadas, la ficha de un vendedor, Idealista) sin montar un teléfono nuevo
// cada vez: uno solo, vivo mientras el CRM esté abierto.

const CtxTelefono = createContext(null);
export const useTelefono = () => useContext(CtxTelefono);

export function ProveedorTelefono({ children, orgId }) {
  const [config, setConfig] = useState(null);
  const [registrado, setRegistrado] = useState(false);
  const [llamada, setLlamada] = useState(null);   // {numero, nombre, estado, comoQuien, segundos}
  const [silencio, setSilencio] = useState(false);
  const ua = useRef(null);
  const sesion = useRef(null);
  const audio = useRef(null);
  const cronometro = useRef(null);

  // ── Arrancar el teléfono ──────────────────────────────────────────────────
  useEffect(() => {
    let vivo = true;
    (async () => {
      let cfg;
      try {
        const { data } = await axios.get(`${API}/telefono/config`);
        if (!data.disponible) return;
        cfg = data;
      } catch (e) { return; }
      if (!vivo) return;
      setConfig(cfg);

      let JsSIP;
      try {
        JsSIP = (await import('jssip')).default;
      } catch (e) {
        console.error('[telefono] no se pudo cargar JsSIP', e);
        return;
      }
      JsSIP.debug.disable();

      const socket = new JsSIP.WebSocketInterface(cfg.ws);
      const agente = new JsSIP.UA({
        sockets: [socket],
        uri: cfg.uri,
        password: cfg.clave,
        display_name: 'eHome',
        register: true,
        session_timers: false,
      });

      agente.on('registered', () => vivo && setRegistrado(true));
      agente.on('unregistered', () => vivo && setRegistrado(false));
      agente.on('registrationFailed', (e) => {
        if (!vivo) return;
        setRegistrado(false);
        console.error('[telefono] no se pudo registrar:', e.cause);
      });

      agente.start();
      ua.current = agente;
    })();
    return () => {
      vivo = false;
      try { ua.current?.stop(); } catch (e) {}
      clearInterval(cronometro.current);
    };
  }, []);

  const soltar = useCallback(() => {
    clearInterval(cronometro.current);
    sesion.current = null;
    setLlamada(null);
    setSilencio(false);
    if (audio.current) { audio.current.srcObject = null; }
  }, []);

  // Sacar la voz del otro por los altavoces.
  //
  // Se llama dos veces a proposito (al crearse la conexion y al descolgar): el
  // evento 'peerconnection' puede haber pasado ya cuando nos enganchamos, y
  // entonces el 'track' no lo ve nadie y no se oye nada. La segunda pasada
  // recoge las pistas que ya estuvieran puestas.
  const engancharAudio = useCallback((s) => {
    const pc = s?.connection;
    if (!pc) return;
    const poner = (stream) => {
      if (!audio.current || !stream) return;
      if (audio.current.srcObject === stream) return;
      audio.current.srcObject = stream;
      audio.current.play().catch(() => {});
    };
    if (!pc._ehomeEnganchado) {
      pc._ehomeEnganchado = true;
      pc.addEventListener('track', (ev) => poner(ev.streams?.[0] || new MediaStream([ev.track])));
    }
    const pistas = (pc.getReceivers?.() || [])
      .map((r) => r.track).filter((t) => t && t.kind === 'audio');
    if (pistas.length) poner(new MediaStream(pistas));
  }, []);

  /**
   * Llamar a un número.
   * @param numero  el teléfono, como esté escrito
   * @param datos   {nombre, comoQuien, sellerId, buyerId, contactId, listingId, chatId}
   */
  const llamar = useCallback(async (numero, datos = {}) => {
    if (!ua.current || !registrado) {
      toast.error('El teléfono todavía no está listo');
      return;
    }
    if (sesion.current) {
      toast.error('Ya hay una llamada en curso');
      return;
    }
    const limpio = String(numero || '').replace(/[^\d+]/g, '');
    if (limpio.replace(/\D/g, '').length < 9) {
      toast.error('Ese número no parece correcto');
      return;
    }

    // Se avisa al servidor ANTES de marcar: así, si la llamada se corta o el
    // navegador se cierra, en la ficha queda que se intentó.
    let idLlamada = null, nombre = datos.nombre || null;
    try {
      const { data } = await axios.post(`${API}/telefono/iniciada`, {
        telefono: limpio, llamado_como: datos.comoQuien || null,
        seller_id: datos.sellerId || null, buyer_id: datos.buyerId || null,
        contact_id: datos.contactId || null, listing_id: datos.listingId || null,
        chat_id: datos.chatId || null, organization_id: orgId,
      });
      idLlamada = data.llamada_id;
      nombre = nombre || data.nombre;
    } catch (e) { /* si el registro falla, la llamada se hace igual */ }

    setLlamada({ numero: limpio, nombre, estado: 'marcando',
                 comoQuien: datos.comoQuien || null, segundos: 0, idLlamada });

    try {
      const s = ua.current.call(`sip:${limpio}@${config.dominio}`, {
        mediaConstraints: { audio: true, video: false },
        // Asterisk lee esta cabecera para saber quién llama y guardarlo.
        extraHeaders: datos.comoQuien ? [`X-Caller-Name: ${datos.comoQuien}`] : [],
        pcConfig: {
          rtcpMuxPolicy: 'require',
          // Sin STUN el navegador solo anuncia su IP de casa (192.168.x.x) y
          // Asterisk no sabe adonde mandar la voz: se queda la llamada muda con
          // "Error sending STUN request" en el log. Con esto el navegador
          // descubre su IP publica y el audio encuentra el camino.
          iceServers: [
            { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
          ],
        },
      });
      sesion.current = s;

      s.on('progress', () => setLlamada((l) => l && { ...l, estado: 'sonando' }));
      s.on('confirmed', () => {
        engancharAudio(s);
        setLlamada((l) => l && { ...l, estado: 'hablando' });
        cronometro.current = setInterval(
          () => setLlamada((l) => (l ? { ...l, segundos: l.segundos + 1 } : l)), 1000);
      });
      s.on('failed', (e) => {
        toast.error(`No se pudo conectar${e?.cause ? `: ${e.cause}` : ''}`);
        soltar();
      });
      s.on('ended', () => { toast.success('Llamada terminada'); soltar(); });
      s.on('peerconnection', () => engancharAudio(s));
    } catch (e) {
      toast.error('No se pudo iniciar la llamada');
      soltar();
    }
  }, [registrado, config, orgId, soltar, engancharAudio]);

  const colgar = useCallback(() => {
    try { sesion.current?.terminate(); } catch (e) {}
    soltar();
  }, [soltar]);

  const alternarSilencio = useCallback(() => {
    const s = sesion.current;
    if (!s) return;
    if (silencio) { s.unmute({ audio: true }); } else { s.mute({ audio: true }); }
    setSilencio(!silencio);
  }, [silencio]);

  const mmss = (n) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;

  return (
    <CtxTelefono.Provider value={{ llamar, colgar, registrado, disponible: !!config, llamada }}>
      {children}
      <audio ref={audio} autoPlay playsInline />

      {llamada && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 3000, width: 258,
                      background: '#0f172a', border: `1px solid ${ORO}55`, borderRadius: 14,
                      padding: 16, boxShadow: '0 12px 40px rgba(0,0,0,0.55)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase',
                           color: llamada.estado === 'hablando' ? '#10b981' : ORO }}>
              {llamada.estado === 'marcando' ? 'Marcando…'
                : llamada.estado === 'sonando' ? 'Llamando…'
                : mmss(llamada.segundos)}
            </span>
            {llamada.estado !== 'hablando' && (
              <Loader2 size={13} style={{ color: ORO }} className="animate-spin" />
            )}
          </div>

          <p style={{ margin: 0, fontSize: 15, color: '#e2e8f0', fontWeight: 600 }}>
            {llamada.nombre || llamada.numero}
          </p>
          {llamada.nombre && (
            <p style={{ margin: '1px 0 0', fontSize: 11.5, color: '#64748b' }}>{llamada.numero}</p>
          )}
          {llamada.comoQuien && (
            <p style={{ margin: '5px 0 0', fontSize: 10.5, color: '#475569' }}>
              llamando como <b style={{ color: ORO }}>{llamada.comoQuien}</b>
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 13 }}>
            <button onClick={alternarSilencio} disabled={llamada.estado !== 'hablando'}
              title={silencio ? 'Activar micrófono' : 'Silenciar'}
              style={{ flex: 1, padding: '9px', borderRadius: 9, border: '1px solid #1e293b',
                       background: silencio ? 'rgba(239,68,68,0.15)' : 'transparent',
                       color: silencio ? '#ef4444' : '#94a3b8',
                       cursor: llamada.estado === 'hablando' ? 'pointer' : 'default',
                       opacity: llamada.estado === 'hablando' ? 1 : 0.4,
                       display: 'flex', justifyContent: 'center' }}>
              {silencio ? <MicOff size={15} /> : <Mic size={15} />}
            </button>
            <button onClick={colgar}
              style={{ flex: 2, padding: '9px', borderRadius: 9, border: 'none',
                       background: '#ef4444', color: '#fff', cursor: 'pointer',
                       display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                       fontSize: 12.5, fontWeight: 700 }}>
              <PhoneOff size={15} /> Colgar
            </button>
          </div>
        </div>
      )}
    </CtxTelefono.Provider>
  );
}

/** Los botones M y S: llamar como Marcos o como Sergio. */
export function BotonesLlamar({ telefono, nombre, sellerId, buyerId, contactId, listingId, chatId, compacto }) {
  const tel = useTelefono();
  if (!tel?.disponible || !telefono) return null;

  const marcar = (quien) => tel.llamar(telefono, {
    nombre, comoQuien: quien, sellerId, buyerId, contactId, listingId, chatId,
  });

  const estilo = (color) => ({
    width: compacto ? 26 : 30, height: compacto ? 26 : 30, borderRadius: '50%',
    border: `1px solid ${color}55`, background: `${color}18`, color,
    cursor: tel.registrado ? 'pointer' : 'not-allowed',
    opacity: tel.registrado ? 1 : 0.35,
    fontSize: compacto ? 11 : 12, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <div style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}
         title={tel.registrado ? 'Llamar' : 'El teléfono aún no está registrado'}>
      <Phone size={compacto ? 11 : 12} style={{ color: '#475569' }} />
      <button onClick={(e) => { e.stopPropagation(); marcar('Marcos'); }}
        disabled={!tel.registrado} style={estilo(ORO)} title="Llamar como Marcos">M</button>
      <button onClick={(e) => { e.stopPropagation(); marcar('Sergio'); }}
        disabled={!tel.registrado} style={estilo('#60a5fa')} title="Llamar como Sergio">S</button>
    </div>
  );
}
